import React, { useEffect, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import { XAxisTypes } from './reducer';

Chart.register(...registerables);

interface BearingSpringRatesChartProps {
	data: Array<{ load: number; deflection: number }>;
	chartState: Array<any>;
	unitSystem: string | undefined;
	ca90RatingMetric: string | undefined;
	ca90RatingImperial: string | undefined;
	c90RatingMetric: string | undefined;
	c90RatingImperial: string | undefined;
	cupNumber: string | undefined;
	coneNumber: string | undefined;
	series: string | undefined;
	xRadioType: string | undefined;
	yRadioType: string | undefined;
	preload: boolean | undefined;
}

const BearingSpringRatesChart: React.FC<BearingSpringRatesChartProps> = ({
	data,
	chartState,
	unitSystem,
	ca90RatingMetric,
	ca90RatingImperial,
	c90RatingMetric,
	c90RatingImperial,
	cupNumber,
	coneNumber,
	xRadioType,
	yRadioType,
	preload
}) => {
	const chartRef = useRef<HTMLCanvasElement>(null);
	const [mobile, setMobile] = useState(window.innerWidth <= 768);
	const [responsiveHeight, setResponsiveHeight] = useState(mobile ? '480px' : '700px');
	const handleWindowSizeChange = () => {
		setMobile(window.innerWidth <= 768);
	};

	useEffect(() => {
		window.addEventListener('resize', handleWindowSizeChange);
		return () => {
			window.removeEventListener('resize', handleWindowSizeChange);
		};
	}, []);


	useEffect(() => {
		if (!chartRef.current || !data) {
			return;
		}

		const ctx = chartRef.current.getContext('2d');
		if (!ctx) {
			return;
		}

		// Extract data for the chart
		// X Axis data
		let xAxisData = data.map((item) => (item.load).replace(/,/g, ''));
		xAxisData = xAxisData.sort((a, b) => a - b);// X-axis: Load
		let xUnitLabel = unitSystem === 'M' ? '(N)' : '(lbf)';
		let xStepSize = (chartState.xAxisMax - chartState.xAxisMin) / xAxisData.length;

		// Y Axis data
		let yAxisData = data.map((item) => yRadioType === 'deflection' ? item.deflection.replace(/,/g, '') : item.springRate.replace(/,/g, '')); // Y-axis: Deflection
		yAxisData = yAxisData.sort((a, b) => a - b);
		let yUnitLabel = unitSystem === 'M' ? yRadioType === 'deflection' ? '(mm)' : '(N/mm)' : yRadioType === 'deflection' ? '(in)' : '(lbf/in)'
		let yStepSize = (chartState.yAxisMax - chartState.yAxisMin) / yAxisData.length;

		const additionalTextPlugin = {
			id: 'additionalText',
			beforeDraw: (chart: any) => {
				const { ctx, chartArea } = chart;
				const { bottom, left, width } = chartArea;
				const { xRadioType } = chart.options.radioTypes;
				const {
					coneNumber,
					cupNumber,
					ca90RatingMetric,
					ca90RatingImperial,
					c90RatingMetric,
					c90RatingImperial,
					preload, xUnitLabel,
					unitSystem
				} = chart.options.partData;
				let c90Rating = '';
				let ca90Rating = '';

				if (unitSystem == 'M') {
					c90Rating = c90RatingMetric;
					ca90Rating = ca90RatingMetric;
				} else {
					c90Rating = c90RatingImperial;
					ca90Rating = ca90RatingImperial;
				}

				ctx.save();
				ctx.font = `${mobile ? 12 : 14}px Arial`;
				ctx.fillStyle = 'black';
				ctx.textAlign = 'left';
				ctx.clearRect(0, 0, chart.width, chart.height);
				const leftPosition = mobile ? left - 87 : left + 5;

 				ctx.fillText(
 					`${ preload ? '360' : '180' } Load Zone / Line Contact / Brg Race-Roll Effect Only`,
 					leftPosition,
 					bottom + 110,
 				);
 				ctx.fillText(
 					`Cone ${ coneNumber }. Cup ${ cupNumber }. ${ xRadioType === XAxisTypes.radial ? 'C90 = ' + c90Rating : 'Ca90 = ' + ca90Rating } ${ xUnitLabel }.`,
 					leftPosition,
 					bottom + 90,
 				);


				ctx.restore();
			},
		};

		Chart.register(additionalTextPlugin);

		const chart = new Chart(ctx, {
			type: 'line',
			data: {
				labels: xAxisData,
				datasets: [
					{
						label: chartState.title,
						data: yAxisData,
						borderColor: 'red',
						backgroundColor: 'black',
						pointStyle: 'rect',
						pointRadius: 3,
						pointBackgroundColor: 'transparent',
						borderWidth: 2,
						tension: 0.1,
					},
				],
			},
			options: {
				radioTypes: {
					xRadioType: xRadioType,
				},
				partData: {
					cupNumber: cupNumber,
					coneNumber: coneNumber,
					ca90RatingMetric: ca90RatingMetric,
					ca90RatingImperial: ca90RatingImperial,
					c90RatingMetric: c90RatingMetric,
					c90RatingImperial: c90RatingImperial,
					preload: preload,
					xUnitLabel: xUnitLabel,
					unitSystem: unitSystem,
					mobile,
				},
				responsive: true,
				layout: {
					padding: {
						bottom: 80,
						top: 80,
					},
				},
				plugins: {
					legend: {
						display: false,
					},
					title: {
						display: true,
						text: chartState.graphTitle,
						color: 'black',
						font: {
							size: 16,
						},
					},
				},
				scales: {
					x: {
						title: {
							display: true,
							text: `${chartState.xAxisLabel} ${xUnitLabel}`,
							color: 'black',
							font: {
								size: 14,
							},
						},
						type: 'linear',
						min: chartState.xAxisMin,
						max: chartState.xAxisMax,
						ticks: {
							format: { maximumFractionDigits: 0, minimumFractionDigits: 0 },
							source: "xAxisData",
							autoSkip: false,
						},
						afterBuildTicks: function (axis) {
							const min = chartState.xAxisMin;
							const max = chartState.xAxisMax;

							let ticks = [];
							for (let value = min; value <= max; value += xStepSize) {
								ticks.push({ value });
							}
							ticks.push({ value: max });
							axis.ticks = ticks;
						},
						grid: {
							color: 'black',
						},
					},
					y: {
						title: {
							display: true,
							text: `${chartState.yAxisLabel} ${yUnitLabel} \n${chartState.yAxisScaling ? '[Log 10]' : '[Linear]'}`,
							color: 'black',
							font: {
								size: 16,
							},
						},
						min: chartState.yAxisMin,
						max: chartState.yAxisMax,
						type: chartState.yAxisScaling ? 'logarithmic' : 'linear',
						grid: {
							color: 'black',
						},
						ticks: {
							autoSkip: false,
							source: "yAxisData",
							callback: function (value, index, values) {
								// If the value is less than 1, show 6 decimal places. Otherwise show none
								if (value < 1) {
									value = parseFloat(value).toFixed(6);
								} else {
									value = parseFloat(value).toFixed(0);
								}
								return value;
							}
						},
						afterBuildTicks: function (axis) {
							let min = chartState.yAxisMin;
							let max = chartState.yAxisMax;
							let ticks = [];

							if (chartState.yAxisScaling) {
								min = Math.log10(chartState.yAxisMin);
								max = Math.log10(chartState.yAxisMax);
								const yStepSize = (max - min) / (yAxisData.length);
								for (let i = 0; i < 10; i++) {
									const tickValue = Math.pow(10, min + i * yStepSize);
									ticks.push({ value: tickValue });
								}
								ticks.push({ value: max });
							} else {
								for (let value = min; value <= max; value += yStepSize) {
									ticks.push({ value: value });
								}
								ticks.push({ value: max });
							}

							axis.ticks = ticks;
						}
					},
				},
			},
		});

		return () => {
			chart.destroy();
		};
	}, [data, chartState.xAxisLabel, chartState.yAxisLabel, chartState.graphTitle]);

	return <canvas ref={chartRef} style={{ width: '100%', height: responsiveHeight, maxHeight: responsiveHeight, padding: '10px' }} />;
};

export default BearingSpringRatesChart;
